'use client'

import { Button } from '@/core/components/form/button/button'
import { useState } from 'react'
import { LuMapPin, LuPlay } from 'react-icons/lu'

import Carousel from '@/core/components/optional/carousel'
import { cn } from '@/core/utils/common'

import CollectionCard from '@/features/veluvit/components/collection/collection-card'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import { albumViews } from '@/features/veluvit/lib/albums'
import { gridItems } from '@/features/veluvit/lib/rules'
import { enterBranch } from '@/features/veluvit/services/chat.actions'
import { useRoom } from '@/features/veluvit/stores/session.store'

/* [2차 개발] 컬렉션 보상 구간(%) — 10% 해금권 · 100% 칭호 (50% 로얄 해금권 선택은 로얄과 함께 없앴다, 2026-10-07)
const MILESTONES = [10, 100] */

/* 글자 — PC 13 이상(2026-10-07 "PC 에서 너무 작다": 레일이 11 · 13 이었다). 레일(sm)은 PC 에만 보이므로 PC 값 하나 */
const SIZE = {
  /** 상세 화면 · 대화 메뉴 시트 */
  md: {
    slide: 'flex-[0_0_104px] over10:flex-[0_0_128px]',
    title: 'text-c13 over10:text-c14',
    head: 'text-c15 over10:text-c17',
    meta: 'text-c12 over10:text-c14',
  },
  /** PC 대화방 옆 레일 */
  sm: {
    slide: 'flex-[0_0_96px]',
    title: 'text-c13',
    head: 'text-c15',
    meta: 'text-c13',
  },
}

/**
 * 작품 컬렉션 — 장소 → 장면(2026-10-07 앨범 · 컬렉션 병합. 기획 studio-renew.md §8.3).
 * 컬렉션은 작품에만 있다 — 예전 /collection 목록 · 캐릭터별 컬렉션 페이지는 없앴다. 캐릭터 쪽은 상황 · 표정 이미지(스튜디오 캐릭터 › 상황 이미지)라 모으는 대상이 아니다.
 *
 * - 위: 모은 장면 수 + 막대 + 다음 보상까지.
 * - 장소마다 한 묶음 — 📍 장소 이름(늘 보인다) · 모은 수, 그 아래 장면 카드를 가로로. 카드를 누르면 해금 · 크게 보기(CollectionCard).
 * - 장소에 자유 진입 장면(work.branches)이 있으면 묶음 오른쪽에 "이 장면부터" — 이전 진행과 상관없이 그 장면으로 대화를 연다(무료).
 * 상세 화면 · 대화 메뉴 시트 · PC 대화방 레일이 같은 부품을 쓴다. 가로 줄을 화면 끝까지 밀지는 쓰는 쪽이 rowClassName 으로 정한다.
 */
export default function WorkCollection({
  workId,
  size = 'md',
  rowClassName,
}: {
  workId: string
  size?: keyof typeof SIZE
  rowClassName?: string
}) {
  const { catalog, ownership, thumb } = useAppData()
  const [entering, setEntering] = useState<string | null>(null)
  const room = useRoom(workId)
  const places = albumViews(
    catalog,
    workId,
    room,
    ownership.unlocked,
    thumb(workId)
  )
  if (!places.length) return null

  const s = SIZE[size]
  const total = gridItems(catalog, workId).length
  const sum = ownership.owned[workId] || 0
  const pct = total ? (sum / total) * 100 : 0
  // const next = MILESTONES.find((m) => pct < m) — [2차 개발] 달성 보상(10% 해금권)

  return (
    <div className="min-w-0">
      {/* 모은 정도 — 숫자 한 줄 + 얇은 막대 */}
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 text-t3 tabular-nums',
          s.meta
        )}
      >
        <span>
          <b className="font-bold text-t1">{sum}</b> / {total} 장면
        </span>
        {/* [2차 개발] 달성 보상 안내 — 보상(10% 해금권)이 해금권과 함께 2차로 넘어갔다(2026-10-08). 되살릴 땐 이 줄 · MILESTONES · next 주석을 푼다
        <span>{next ? `다음 보상까지 ${Math.max(1, Math.ceil((total * next) / 100) - sum)}장` : '모든 보상을 받았어요'}</span> */}
      </div>
      <div
        aria-hidden="true"
        className="mt-1.5 h-1 overflow-hidden rounded-full bg-foreground/[0.08]"
      >
        <span
          style={{ width: `${pct}%` }}
          className="block h-full rounded-full bg-point"
        />
      </div>

      {/* 장소 묶음 — 묶음 사이에 옅은 선 하나로 경계를 준다(선 없이 이어지니 전체가 한 덩어리로 보였다) */}
      <div className="mt-4 grid grid-cols-1">
        {places.map((place) => (
          <section
            key={place.id}
            aria-label={`${place.place} 장면`}
            className={cn(
              'min-w-0 border-t border-foreground/[0.07] first:border-t-0 first:pt-0',
              size === 'sm' ? 'py-3.5' : 'py-5',
              'last:pb-0'
            )}
          >
            <header className="flex items-center gap-1.5">
              <LuMapPin
                aria-hidden="true"
                className="size-3.5 flex-none text-point"
              />
              <h3 className={cn('min-w-0 truncate font-bold text-t1', s.head)}>
                {place.place}
              </h3>
              <span className={cn('flex-none text-t3 tabular-nums', s.meta)}>
                {place.owned}/{place.items.length}
              </span>
              {place.branch && (
                <Button
                  type="button"
                  disabled={entering !== null}
                  onClick={() => {
                    const branch = place.branch
                    if (!branch) return
                    setEntering(branch.id)
                    void enterBranch(catalog, workId, branch.id).finally(() =>
                      setEntering(null)
                    )
                  }}
                  className="ml-auto inline-flex flex-none cursor-pointer items-center gap-1 rounded-full bg-foreground/[0.08] px-2.5 py-1 text-c12 font-semibold text-t1 over10:text-c13 transition-colors hover:bg-foreground/[0.14] disabled:cursor-progress disabled:opacity-60"
                  variant="bare"
                >
                  <LuPlay aria-hidden="true" className="size-3" />
                  {entering === place.branch.id ? '여는 중…' : '이 장면부터'}
                </Button>
              )}
            </header>
            {place.items.length ? (
              <Carousel
                ariaLabel={`${place.place} 앨범 장면`}
                className={cn('mt-2 min-w-0', rowClassName)}
                classNames={{ track: 'gap-2 pb-0.5' }}
                slideBasis={s.slide}
                showDots={false}
                wheelGestures
                dragFree
                slides={place.items.map((item) => {
                  const open =
                    ownership.unlocked.has(`${workId}:${item.i}`) && !item.adult
                  return {
                    id: item.cardId,
                    content: (
                      <div>
                        <CollectionCard
                          workId={workId}
                          item={item}
                          small={size === 'sm'}
                          bare
                        />
                        {/* 장면 제목 — 연 장면은 제목, 잠긴 장면은 흐린 글자 대신 '잠긴 장면'(그림이 이미 흐려 글자까지 흐리면 통째로 뿌옇다) */}
                        <p
                          className={cn(
                            'mt-1.25 truncate',
                            s.title,
                            open ? 'text-t1' : 'text-t4'
                          )}
                        >
                          {open ? item.title || `장면 ${item.no}` : '잠긴 장면'}
                        </p>
                      </div>
                    ),
                  }
                })}
              />
            ) : (
              <p className={cn('mt-2 text-t3', s.meta)}>
                이 장소의 장면을 준비하고 있어요.
              </p>
            )}
          </section>
        ))}
      </div>
    </div>
  )
}
